import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export default function AdminDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [contractors, setContractors] = useState([])
  const [stats, setStats] = useState({
    totalContractors: 0,
    activeSubscriptions: 0,
    trialing: 0,
    totalAssessments: 0,
    totalRevenue: 0,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [trialModalOpen, setTrialModalOpen] = useState(false)
  const [selectedContractor, setSelectedContractor] = useState(null)
  const [newTrialDate, setNewTrialDate] = useState('')
  const [updating, setUpdating] = useState(false)
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    // Check if user is admin
    if (user && user.role !== 'admin') {
      navigate('/dashboard')
      return
    }

    if (user) {
      fetchContractors()
      fetchStats()
    }
  }, [user, navigate])

  const fetchContractors = async () => {
    try {
      setLoading(true)

      // Fetch all contractors with related data
      const { data, error } = await supabase
        .from('contractors')
        .select(`
          *,
          users(count),
          assessments(count)
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      setContractors(data || [])
    } catch (error) {
      console.error('Error fetching contractors:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      // Get total contractors
      const { count: totalContractors } = await supabase
        .from('contractors')
        .select('*', { count: 'exact', head: true })

      // Get active subscriptions
      const { count: activeSubscriptions } = await supabase
        .from('contractors')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'active')

      // Get trialing subscriptions
      const { count: trialing } = await supabase
        .from('contractors')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'trialing')

      // Get total assessments
      const { count: totalAssessments } = await supabase
        .from('assessments')
        .select('*', { count: 'exact', head: true })

      setStats({
        totalContractors: totalContractors || 0,
        activeSubscriptions: activeSubscriptions || 0,
        trialing: trialing || 0,
        totalAssessments: totalAssessments || 0,
        totalRevenue: (activeSubscriptions || 0) * 1299,
      })
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const filteredContractors = contractors.filter(contractor => {
    const matchesSearch = contractor.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || contractor.subscription_status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      trialing: 'bg-blue-100 text-blue-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getDaysRemaining = (trialEndsAt) => {
    if (!trialEndsAt) return null
    const now = new Date()
    const endDate = new Date(trialEndsAt)
    const diffTime = endDate - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getTrialStatusColor = (daysRemaining) => {
    if (daysRemaining === null) return 'text-gray-500'
    if (daysRemaining < 0) return 'text-red-600'
    if (daysRemaining <= 3) return 'text-orange-600'
    if (daysRemaining <= 7) return 'text-yellow-600'
    return 'text-green-600'
  }

  const openTrialModal = (contractor) => {
    setSelectedContractor(contractor)
    // Format current date for datetime-local input
    if (contractor.trial_ends_at) {
      const date = new Date(contractor.trial_ends_at)
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      setNewTrialDate(localDate.toISOString().slice(0, 16))
    } else {
      // Default to 7 days from now if no trial date set
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 7)
      const localDate = new Date(defaultDate.getTime() - defaultDate.getTimezoneOffset() * 60000)
      setNewTrialDate(localDate.toISOString().slice(0, 16))
    }
    setTrialModalOpen(true)
  }

  const closeTrialModal = () => {
    setTrialModalOpen(false)
    setSelectedContractor(null)
    setNewTrialDate('')
  }

  const setQuickDate = (days) => {
    const date = new Date()
    if (days !== null) {
      date.setDate(date.getDate() + days)
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    setNewTrialDate(localDate.toISOString().slice(0, 16))
  }

  const handleUpdateTrialDate = async () => {
    if (!selectedContractor || !newTrialDate) return

    try {
      setUpdating(true)

      // Convert local datetime to UTC timestamp
      const trialDate = new Date(newTrialDate).toISOString()

      const { data, error } = await supabase
        .rpc('update_trial_end_date_admin', {
          p_contractor_id: selectedContractor.id,
          p_new_trial_end_date: trialDate
        })

      if (error) throw error

      // Show success notification
      setNotification({
        type: 'success',
        message: `Trial date updated successfully for ${selectedContractor.company_name}`
      })

      // Refresh contractors list
      await fetchContractors()

      // Close modal
      closeTrialModal()

      // Clear notification after 3 seconds
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error updating trial date:', error)
      setNotification({
        type: 'error',
        message: `Failed to update trial date: ${error.message}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-xl text-gray-600">Loading admin dashboard...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage contractors, subscriptions, and platform analytics</p>
          <div className="mt-4 flex gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/rebates')}
              className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-lg transition"
            >
              Manage Rebates
            </button>
            <button
              onClick={() => navigate('/admin/analytics')}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white rounded-lg transition"
            >
              View Analytics
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-3xl font-bold text-gray-800">{stats.totalContractors}</div>
            <div className="text-gray-600 mt-1">Total Contractors</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-3xl font-bold text-green-600">{stats.activeSubscriptions}</div>
            <div className="text-gray-600 mt-1">Active Subscriptions</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-3xl font-bold text-blue-600">{stats.trialing}</div>
            <div className="text-gray-600 mt-1">In Trial</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-3xl font-bold text-purple-600">{stats.totalAssessments}</div>
            <div className="text-gray-600 mt-1">Total Assessments</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-3xl font-bold text-indigo-600">${stats.totalRevenue.toLocaleString()}</div>
            <div className="text-gray-600 mt-1">Monthly Revenue</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Contractors
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by company name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="md:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contractors Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trial Ends
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContractors.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      No contractors found
                    </td>
                  </tr>
                ) : (
                  filteredContractors.map((contractor) => (
                    <tr key={contractor.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {contractor.company_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {contractor.city}, {contractor.state}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(contractor.subscription_status)}`}>
                          {contractor.subscription_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {contractor.assessments_this_period} / {contractor.included_assessments}
                        </div>
                        {contractor.assessments_this_period > contractor.included_assessments && (
                          <div className="text-xs text-orange-600">
                            +{contractor.assessments_this_period - contractor.included_assessments} overage
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contractor.users?.[0]?.count || 0} users
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(contractor.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(contractor.trial_ends_at)}
                        </div>
                        {contractor.trial_ends_at && (
                          <div className={`text-xs ${getTrialStatusColor(getDaysRemaining(contractor.trial_ends_at))}`}>
                            {getDaysRemaining(contractor.trial_ends_at) > 0
                              ? `${getDaysRemaining(contractor.trial_ends_at)} days left`
                              : getDaysRemaining(contractor.trial_ends_at) === 0
                              ? 'Expires today'
                              : `Expired ${Math.abs(getDaysRemaining(contractor.trial_ends_at))} days ago`
                            }
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openTrialModal(contractor)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                          title="Edit trial date"
                        >
                          Edit Trial
                        </button>
                        <button
                          onClick={() => navigate(`/admin/contractors/${contractor.id}`)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Admin Dashboard</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Monitor all contractor subscriptions and usage</li>
            <li>• Manage regional rebate configurations</li>
            <li>• View platform-wide analytics and reports</li>
            <li>• Track trial conversions and revenue metrics</li>
            <li>• Edit trial dates to extend or modify trial periods</li>
          </ul>
        </div>
      </div>

      {/* Trial Edit Modal */}
      {trialModalOpen && selectedContractor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Edit Trial Period</h2>
              <button
                onClick={closeTrialModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                disabled={updating}
              >
                ×
              </button>
            </div>

            <div className="mb-6">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Company: <span className="font-bold">{selectedContractor.company_name}</span>
              </div>

              {selectedContractor.trial_ends_at && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="text-sm text-gray-600">Current Trial End Date:</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {formatDate(selectedContractor.trial_ends_at)}
                  </div>
                  <div className={`text-sm ${getTrialStatusColor(getDaysRemaining(selectedContractor.trial_ends_at))}`}>
                    {getDaysRemaining(selectedContractor.trial_ends_at) > 0
                      ? `${getDaysRemaining(selectedContractor.trial_ends_at)} days remaining`
                      : getDaysRemaining(selectedContractor.trial_ends_at) === 0
                      ? 'Expires today'
                      : `Expired ${Math.abs(getDaysRemaining(selectedContractor.trial_ends_at))} days ago`
                    }
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Presets:
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setQuickDate(7)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                    disabled={updating}
                  >
                    +7 days
                  </button>
                  <button
                    onClick={() => setQuickDate(30)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                    disabled={updating}
                  >
                    +30 days
                  </button>
                  <button
                    onClick={() => setQuickDate(90)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                    disabled={updating}
                  >
                    +90 days
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="trial-date" className="block text-sm font-medium text-gray-700 mb-2">
                  New Trial End Date:
                </label>
                <input
                  id="trial-date"
                  type="datetime-local"
                  value={newTrialDate}
                  onChange={(e) => setNewTrialDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={updating}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeTrialModal}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition"
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTrialDate}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition disabled:bg-purple-300"
                disabled={updating || !newTrialDate}
              >
                {updating ? 'Updating...' : 'Update Trial Date'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className={`rounded-lg shadow-lg p-4 max-w-md ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className={`ml-4 flex-shrink-0 ${
                  notification.type === 'success' ? 'text-green-400 hover:text-green-600' : 'text-red-400 hover:text-red-600'
                }`}
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
